/**
 * "Copy for email" — the benchmark rates as a paste-ready email block.
 *
 * Two flavours of the same content: an HTML table for rich editors (Gmail
 * keeps inline styles but drops <style> blocks and CSS variables, so every
 * style is inline with literal colors) and plain-text bullets for anything
 * that only takes text. Client-facing: no internal stats, ids or placeholders
 * markers — just the labels, rates, ranges and notes as saved.
 */

export interface EmailBenchmarkRow {
  label: string;
  ctr: string;
  ctrRange: string;
  vcr: string;
  vcrRange: string;
  note?: string;
}

export interface BenchmarkEmailInput {
  title: string;
  rows: EmailBenchmarkRow[];
  /** Optional line under the title, e.g. the data window the rates come from. */
  subtitle?: string;
  /** Optional closing line, e.g. how the benchmarks are calculated. */
  footnote?: string;
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const isNa = (value: string): boolean => /^n\/?a$/i.test(value.trim());

/** "0.05% (range 0.03% – 0.07%)" — the range is left out when it adds nothing. */
function rateWithRange(value: string, range: string): string {
  if (!range || isNa(range) || range.trim() === value.trim()) return value;
  return `${value} (range ${range})`;
}

/** Distinct notes in row order, each tied to the first row that carries it. */
function footnotes(rows: EmailBenchmarkRow[]): { label: string; note: string }[] {
  const seen = new Set<string>();
  const out: { label: string; note: string }[] = [];
  for (const row of rows) {
    const note = (row.note || '').trim();
    if (!note || seen.has(note)) continue;
    seen.add(note);
    out.push({ label: row.label, note });
  }
  return out;
}

const COLUMNS: { heading: string; cell: (row: EmailBenchmarkRow) => string }[] = [
  { heading: 'Ad Unit', cell: (row) => row.label },
  { heading: 'CTR', cell: (row) => row.ctr },
  { heading: 'CTR Range', cell: (row) => row.ctrRange },
  { heading: 'Completion Rate', cell: (row) => row.vcr },
  { heading: 'Completion Rate Range', cell: (row) => row.vcrRange },
];

// Literal values of the brand neutrals (email clients ignore CSS variables):
// --cg-neutral-200 border, --cg-neutral-50 header, --cg-neutral-800 text,
// --cg-neutral-500 secondary text.
const BORDER = '#DDD9CD';
const HEADER_BG = '#F5F3EE';
const TEXT = '#18181A';
const MUTED = '#6B675C';
const CELL = `padding:6px 10px;border:1px solid ${BORDER};text-align:left;vertical-align:top;`;

export function buildBenchmarkEmail({ title, rows, subtitle, footnote }: BenchmarkEmailInput): { html: string; text: string } {
  const notes = footnotes(rows);

  const textLines: string[] = [title];
  if (subtitle) textLines.push(subtitle);
  for (const row of rows) {
    textLines.push('', row.label);
    textLines.push(`• CTR: ${rateWithRange(row.ctr, row.ctrRange)}`);
    textLines.push(`• Completion Rate: ${rateWithRange(row.vcr, row.vcrRange)}`);
  }
  if (notes.length > 0) {
    textLines.push('');
    for (const { label, note } of notes) textLines.push(`Note (${label}): ${note}`);
  }
  if (footnote) textLines.push('', footnote);

  const head = COLUMNS.map(
    (column) => `<th style="${CELL}background:${HEADER_BG};font-weight:bold;">${escapeHtml(column.heading)}</th>`,
  ).join('');
  const body = rows
    .map(
      (row) =>
        `<tr>${COLUMNS.map((column, index) => {
          const weight = index === 0 ? 'font-weight:bold;' : '';
          return `<td style="${CELL}${weight}">${escapeHtml(column.cell(row))}</td>`;
        }).join('')}</tr>`,
    )
    .join('');
  const htmlParts = [
    `<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;color:${TEXT};">`,
    `<p style="margin:0 0 4px;font-size:16px;font-weight:bold;">${escapeHtml(title)}</p>`,
    subtitle ? `<p style="margin:0 0 10px;font-size:13px;color:${MUTED};">${escapeHtml(subtitle)}</p>` : '',
    `<table cellpadding="0" cellspacing="0" style="border-collapse:collapse;font-size:13px;margin-top:6px;">`,
    `<thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`,
    ...notes.map(
      ({ label, note }) =>
        `<p style="margin:8px 0 0;font-size:12px;color:${MUTED};">Note (${escapeHtml(label)}): ${escapeHtml(note)}</p>`,
    ),
    footnote ? `<p style="margin:10px 0 0;font-size:12px;color:${MUTED};">${escapeHtml(footnote)}</p>` : '',
    '</div>',
  ];

  return { html: htmlParts.join(''), text: textLines.join('\n') };
}
